import * as React from "react";
import { Tabs } from "../../../bits/Tabs/Tabs";
import CallHistory from "../CallHistory/CallHistory";
import OutboundEmails from "../OutboundEmails/OutboundEmails";
import SmsHistory, { mergeSmsHistory } from "../SmsHistory/SmsHistory";
import LeaConversation from "../LeaConversation/LeaConversation";
import { useLeaWorkflow } from "../LeaConversation/LeaWorkflowContext";
import styles from "./CommsWrapper.module.scss";
import {
    CallStoreResponse,
    IClient,
    ICommunicationNote,
    CommunicationNotesRequest,
    ICommunicationSummary,
    IOutboundSms,
    MessageStoreResponse,
    OutboundEmailStatus,
} from "intake_shared/src/types";
import { httpsCallable } from "firebase/functions";
import { fxs } from "../../../../../utils/firestore";
import store from "../../../../../store";
import phone from "phone";
import { useToasts } from "react-toast-notifications";

interface CommsWrapperProps {
    client_id: string;
    client: IClient;
    communicationSummary?: ICommunicationSummary;
    onSummaryChange?: (summary: ICommunicationSummary) => void;
}

export function getCommunicationTabCount(
    liveCount: number | undefined,
    cachedCount: number | undefined,
    truncated: boolean,
    hasError: boolean,
    isLoading = false
): number | string {
    const count = !isLoading && !hasError ? liveCount ?? cachedCount : cachedCount;

    if (count !== undefined) {
        return truncated ? `${count}+` : count || "0";
    }

    return hasError ? "—" : "…";
}

export default function CommsWrapper({
    client_id,
    client,
    communicationSummary,
    onSummaryChange,
}: CommsWrapperProps) {
    const { addToast } = useToasts();
    const [notesState, setNotesState] = React.useState<{
        clientId: string;
        notes: ICommunicationNote[];
    }>({ clientId: client_id, notes: [] });
    const notesRequest = React.useRef(0);
    const currentClientId = React.useRef(client_id);
    currentClientId.current = client_id;
    const notes = notesState.clientId === client_id ? notesState.notes : [];
    const callNotes = React.useCallback(
        async (request: CommunicationNotesRequest) => {
            const callable = httpsCallable<
                CommunicationNotesRequest,
                ICommunicationNote[] | ICommunicationNote
            >(fxs, "httpFunctionCommunicationNotes");
            return (await callable(request)).data;
        },
        []
    );
    const refreshNotes = React.useCallback(async () => {
        const requestId = ++notesRequest.current;
        try {
            const result: ICommunicationNote[] = [];
            let afterNoteId: string | undefined;
            let page: ICommunicationNote[];
            do {
                const request: CommunicationNotesRequest = {
                    action: "list",
                    clientId: client_id,
                };
                if (afterNoteId !== undefined) {
                    request.afterNoteId = afterNoteId;
                }
                const response = await callNotes(request);
                if (!Array.isArray(response))
                    throw new Error("Invalid notes response");
                page = response;
                result.push(...page);
                afterNoteId = page[page.length - 1]?.note_id;
            } while (
                page.length === 100 &&
                requestId === notesRequest.current &&
                currentClientId.current === client_id
            );
            if (
                requestId === notesRequest.current &&
                currentClientId.current === client_id
            ) {
                setNotesState({ clientId: client_id, notes: result });
            }
            return true;
        } catch {
            if (currentClientId.current === client_id) {
                addToast("Unable to load communication notes. Please try again.", {
                    appearance: "error",
                });
            }
            return false;
        }
    }, [addToast, callNotes, client_id]);
    const mutateNote = React.useCallback(
        async (request: CommunicationNotesRequest) => {
            try {
                const note = await callNotes(request);
                if (Array.isArray(note)) throw new Error("Invalid note response");
                if (currentClientId.current !== client_id) return false;
                ++notesRequest.current;
                setNotesState((previous) => {
                    const currentNotes =
                        previous.clientId === client_id ? previous.notes : [];
                    const updated = currentNotes.filter(
                        (entry) => entry.note_id !== note.note_id
                    );
                    return {
                        clientId: client_id,
                        notes:
                            note.deleted_at === null ? [...updated, note] : updated,
                    };
                });
                return true;
            } catch {
                if (currentClientId.current === client_id) {
                    addToast(
                        "Unable to save communication note. Please try again.",
                        { appearance: "error" }
                    );
                }
                return false;
            }
        },
        [addToast, callNotes, client_id]
    );
    const noteActions = React.useMemo(
        () => ({
            onRefresh: refreshNotes,
            onCreate: (
                channel: ICommunicationNote["channel"],
                recordId: string,
                body: string
            ) =>
                mutateNote({
                    action: "create",
                    clientId: client_id,
                    channel,
                    recordId,
                    body,
                }),
            onUpdate: (noteId: string, body: string) =>
                mutateNote({ action: "update", clientId: client_id, noteId, body }),
            onDelete: (noteId: string) =>
                mutateNote({ action: "delete", clientId: client_id, noteId }),
        }),
        [client_id, mutateNote, refreshNotes]
    );
    React.useEffect(() => {
        void refreshNotes();
    }, [refreshNotes]);
    const [ringCentralSmsLoading, setRingCentralSmsLoading] = React.useState(false);
    const [outboundSmsLoading, setOutboundSmsLoading] = React.useState(false);
    const [ringCentralSmsError, setRingCentralSmsError] = React.useState(false);
    const [outboundSmsError, setOutboundSmsError] = React.useState(false);
    const [clientMessages, setClientMessages] =
        React.useState<MessageStoreResponse>();
    const [outboundSms, setOutboundSms] = React.useState<IOutboundSms[]>([]);
    const [callLoading, setCallLoading] = React.useState(false);
    const [callError, setCallError] = React.useState(false);
    const [callLogs, setCallLogs] = React.useState<CallStoreResponse>();
    const [outboundEmailsCount, setOutboundEmailsCount] = React.useState(0);
    const [templatesCount, setTemplatesCount] = React.useState(0);

    const userPhoneNumber = phone(client.phone_no ?? "");
    const normalizedClientPhone = userPhoneNumber.isValid
        ? userPhoneNumber.phoneNumber
        : "";

    const exportGetSmsFunction = httpsCallable(fxs, "httpFunctionExportGetSms");

    const exportGetCallLogsFunction = httpsCallable(
        fxs,
        "httpFunctionExportGetCallLogs"
    );

    const getSms = async (): Promise<MessageStoreResponse | undefined> => {
        setRingCentralSmsLoading(true);
        setRingCentralSmsError(false);
        try {
            const results = await exportGetSmsFunction({ clientId: client_id });
            const messages = results.data as MessageStoreResponse;
            setClientMessages(messages);
            return messages;
        } catch (error) {
            setClientMessages(undefined);
            setRingCentralSmsError(true);
            return undefined;
        } finally {
            setRingCentralSmsLoading(false);
        }
    };

    const getCallLogs = async (): Promise<CallStoreResponse | undefined> => {
        setCallLoading(true);
        setCallError(false);
        try {
            const results = await exportGetCallLogsFunction({
                clientId: client_id,
            });
            const calls = results.data as CallStoreResponse;
            setCallLogs(calls);
            return calls;
        } catch (error) {
            setCallLogs(undefined);
            setCallError(true);
            return undefined;
        } finally {
            setCallLoading(false);
        }
    };

    const fetchSentOutboundSms = React.useCallback(async (): Promise<
        IOutboundSms[] | undefined
    > => {
        if (!normalizedClientPhone) {
            setOutboundSms([]);
            setOutboundSmsError(false);
            setOutboundSmsLoading(false);
            return undefined;
        }

        setOutboundSmsLoading(true);
        setOutboundSmsError(false);
        try {
            const outboundSms = await store.fetchOutboundSms(normalizedClientPhone);
            setOutboundSms(outboundSms);
            return outboundSms;
        } catch (error) {
            setOutboundSms([]);
            setOutboundSmsError(true);
            return undefined;
        } finally {
            setOutboundSmsLoading(false);
        }
    }, [normalizedClientPhone]);

    React.useEffect(() => {
        const loadCommunicationHistory = async () => {
            const [ringCentralMessages, calls, sentOutboundSms] = await Promise.all([
                getSms(),
                getCallLogs(),
                fetchSentOutboundSms(),
            ]);

            if (!normalizedClientPhone) {
                return;
            }

            const summary: ICommunicationSummary = {
                client_id,
                phone_number: normalizedClientPhone,
            };

            if (ringCentralMessages && sentOutboundSms) {
                summary.sms_count = mergeSmsHistory(
                    ringCentralMessages.records,
                    sentOutboundSms
                ).length;
            }
            if (calls) {
                summary.call_count = calls.records.length;
                if (calls.truncated) {
                    summary.call_count_truncated = true;
                }
            }
            if (
                summary.sms_count === undefined &&
                summary.call_count === undefined
            ) {
                return;
            }

            onSummaryChange?.(summary);
            store.updateCommunicationSummary(summary).catch((error: unknown) => {
                console.warn("Unable to cache communication summary", {
                    clientId: client_id,
                    error: error instanceof Error ? error.message : "Unknown error",
                });
            });
        };

        loadCommunicationHistory();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [client_id, fetchSentOutboundSms, onSummaryChange]);

    const smsMessages = mergeSmsHistory(clientMessages?.records, outboundSms);
    const smsLoading = ringCentralSmsLoading || outboundSmsLoading;
    const smsError = ringCentralSmsError || outboundSmsError;
    const liveSmsCount =
        !smsLoading && !smsError && clientMessages ? smsMessages.length : undefined;
    const smsCount = getCommunicationTabCount(
        liveSmsCount,
        communicationSummary?.sms_count,
        false,
        smsError,
        smsLoading
    );
    const hasLiveCallLogs = !callLoading && !callError && Boolean(callLogs);
    const callCount = getCommunicationTabCount(
        callLogs?.records.length,
        communicationSummary?.call_count,
        hasLiveCallLogs
            ? Boolean(callLogs?.truncated)
            : Boolean(communicationSummary?.call_count_truncated),
        callError,
        callLoading
    );

    const lea = useLeaWorkflow();
    const leaWorkflow = lea?.workflow;

    const tabItems = [
        {
            key: "outbound-emails",
            label: "Sent Emails",
            children: (
                <OutboundEmails
                    client_id={client_id}
                    client={client}
                    statuses={Object.values(OutboundEmailStatus)}
                    onCountChange={setOutboundEmailsCount}
                />
            ),
            count: outboundEmailsCount || "0",
        },
        {
            key: "email-templates",
            label: "All Email Templates",
            children: (
                <OutboundEmails
                    client_id={client_id}
                    client={client}
                    statuses={Object.values(OutboundEmailStatus)}
                    view="templates"
                    onCountChange={setTemplatesCount}
                />
            ),
            count: templatesCount || "0",
        },
        {
            key: "sms-history",
            label: "SMS History",
            children: (
                <SmsHistory
                    key={client_id}
                    messages={smsMessages}
                    smsLoading={smsLoading}
                    smsError={smsError}
                    clientPhone={normalizedClientPhone}
                    notes={notes}
                    noteActions={noteActions}
                />
            ),
            count: smsCount,
        },
        {
            key: "call-history",
            label: "Call History",
            children: (
                <CallHistory
                    key={client_id}
                    callLogs={callLogs as CallStoreResponse}
                    callLoading={callLoading}
                    callError={callError}
                    clientPhone={normalizedClientPhone}
                    notes={notes}
                    noteActions={noteActions}
                />
            ),
            count: callCount,
        },
        {
            key: "lea-ai",
            label: "Lea AI",
            children: <LeaConversation />,
            count: leaWorkflow ? String(leaWorkflow.history.length) : "—",
            // Keep the tab when loading failed so staff can see the error and retry.
            hide: !leaWorkflow && !lea?.error,
        },
    ];

    return (
        <div>
            <Tabs items={tabItems} className={styles.commsTab} />
        </div>
    );
}
